import * as THREE from 'three';
import { v4 as uuidv4 } from 'uuid';
import { LODLevel } from '../optimization/LODSystem';
import { LODSystem } from '../optimization/LODSystem';
import { BoundingVolume } from '../physics/BoundingVolume';

export enum ObjectType {
  STATIC,
  DYNAMIC
}

export class GameObject {
  private id: string;
  private name: string;
  private type: ObjectType;
  
  // Core components
  private mesh: THREE.Mesh | null = null;
  private parent: GameObject | null = null;
  private children: Set<GameObject> = new Set();
  
  // Transform properties
  private position: THREE.Vector3;
  private rotation: THREE.Euler;
  private scale: THREE.Vector3;
  
  // State flags
  private isActive: boolean = true;
  private isVisible: boolean = true;
  
  // LOD properties (optional)
  private hasLOD: boolean = false;
  private currentLODLevel: LODLevel = LODLevel.HIGH;
  private lodMeshes: Map<LODLevel, THREE.Mesh> = new Map();
  
  // Collision properties (optional)
  private boundingVolume: BoundingVolume | null = null;
  private isCollidable: boolean = false;
  
  constructor(name: string = 'GameObject', type: ObjectType = ObjectType.STATIC) {
    this.id = uuidv4();
    this.name = name;
    this.type = type;
    
    // Initialize transform
    this.position = new THREE.Vector3(0, 0, 0);
    this.rotation = new THREE.Euler(0, 0, 0);
    this.scale = new THREE.Vector3(1, 1, 1);
    
    console.log(`GameObject created: ${this.name} (${this.id})`);
  }
  
  // Core methods
  public update(deltaTime: number): void {
    // Base update method - does nothing by default
    // Child classes should override this
    
    // Update bounding volume if it exists
    if (this.boundingVolume) {
      this.boundingVolume.update(this.position);
    }
    
    // Update children
    this.children.forEach(child => {
      if (child.isActive) {
        child.update(deltaTime);
      }
    });
  }
  
  public setMesh(mesh: THREE.Mesh): void {
    this.mesh = mesh;
    
    // Apply current transform to mesh
    this.applyTransform();
  }
  
  // LOD support methods
  public enableLOD(): void {
    this.hasLOD = true;
    // Register with LOD system
    LODSystem.getInstance().registerObject(this.id, this);
  }
  
  public disableLOD(): void {
    this.hasLOD = false;
    // Unregister from LOD system
    LODSystem.getInstance().unregisterObject(this.id);
  }
  
  public setLODMesh(level: LODLevel, mesh: THREE.Mesh): void {
    if (!this.hasLOD) {
      console.warn(`LOD not enabled for ${this.name}. Call enableLOD() first.`);
      return;
    }
    
    // Store the mesh for this LOD level
    this.lodMeshes.set(level, mesh);
    
    // If this is the current LOD level, apply it
    if (level === this.currentLODLevel) {
      this.setMesh(mesh);
    }
    
    // Make sure we're tracking all meshes
    if (!mesh.parent) {
      // If not already in the scene, keep track of it but hide it
      mesh.visible = false;
    }
  }
  
  /**
   * Get the current LOD level
   */
  public getLODLevel(): LODLevel {
    return this.currentLODLevel;
  }
  
  /**
   * Set the current LOD level and update the visible mesh
   */
  public setLODLevel(level: LODLevel): void {
    if (!this.hasLOD) {
      return;
    }
    
    // If the level hasn't changed, do nothing
    if (this.currentLODLevel === level) {
      return;
    }
    
    // Store the new level
    this.currentLODLevel = level;
    
    // Get the mesh for this level
    const mesh = this.lodMeshes.get(level);
    if (mesh) {
      // Hide the current mesh
      const currentMesh = this.getMesh();
      if (currentMesh) {
        currentMesh.visible = false;
      }
      
      // Set the new mesh
      this.setMesh(mesh);
      
      // Make the new mesh visible
      mesh.visible = true;
    } else {
      console.warn(`No mesh available for LOD level ${level} on object ${this.name}`);
    }
  }
  
  /**
   * Check if this object has LOD enabled
   */
  public hasLODEnabled(): boolean {
    return this.hasLOD;
  }
  
  // Transform methods
  public setPosition(x: number, y: number, z: number): void {
    this.position.set(x, y, z);
    this.applyTransform();
    
    // Update bounding volume
    if (this.boundingVolume) {
      this.boundingVolume.update(this.position);
    }
  }
  
  public setRotation(x: number, y: number, z: number): void {
    this.rotation.set(x, y, z);
    this.applyTransform();
  }
  
  public setScale(x: number, y: number, z: number): void {
    this.scale.set(x, y, z);
    this.applyTransform();
  }
  
  protected applyTransform(): void {
    if (this.mesh) {
      this.mesh.position.copy(this.position);
      this.mesh.rotation.copy(this.rotation);
      this.mesh.scale.copy(this.scale);
    }
  }
  
  // Hierarchy methods
  public addChild(child: GameObject): void {
    if (child === this) {
      console.error('Cannot add object as child of itself');
      return;
    }
    
    // Remove from previous parent if exists
    if (child.parent) {
      child.parent.removeChild(child);
    }
    
    this.children.add(child);
    child.parent = this;
  }
  
  public removeChild(child: GameObject): void {
    if (this.children.has(child)) {
      this.children.delete(child);
      child.parent = null;
    }
  }
  
  // State methods
  public activate(): void {
    this.isActive = true;
    if (this.mesh) {
      this.mesh.visible = this.isVisible;
    }
  }
  
  public deactivate(): void {
    this.isActive = false;
    if (this.mesh) {
      this.mesh.visible = false;
    }
  }
  
  public show(): void {
    this.isVisible = true;
    if (this.isActive && this.mesh) {
      this.mesh.visible = true;
    }
  }
  
  public hide(): void {
    this.isVisible = false;
    if (this.mesh) {
      this.mesh.visible = false;
    }
  }
  
  // Getters
  public getId(): string {
    return this.id;
  }
  
  public getName(): string {
    return this.name;
  }
  
  public getType(): ObjectType {
    return this.type;
  }
  
  public getMesh(): THREE.Mesh | null {
    return this.mesh;
  }
  
  public getPosition(): THREE.Vector3 {
    return this.position.clone();
  }
  
  public getRotation(): THREE.Euler {
    return this.rotation.clone();
  }
  
  public getScale(): THREE.Vector3 {
    return this.scale.clone();
  }
  
  public getParent(): GameObject | null {
    return this.parent;
  }
  
  public getChildren(): GameObject[] {
    return Array.from(this.children);
  }
  
  public isActivated(): boolean {
    return this.isActive;
  }
  
  public isShown(): boolean {
    return this.isVisible;
  }
  
  // Bounding volume methods
  /**
   * Set the bounding volume for this object
   * @param volume The bounding volume to use for collision detection
   */
  public setBoundingVolume(volume: BoundingVolume): void {
    this.boundingVolume = volume;
    this.isCollidable = true;
    
    // Update the volume with the current position
    this.boundingVolume.update(this.position);
  }
  
  /**
   * Remove the bounding volume from this object
   */
  public removeBoundingVolume(): void {
    this.boundingVolume = null;
    this.isCollidable = false;
  }
  
  /**
   * Get the bounding volume for this object
   */
  public getBoundingVolume(): BoundingVolume | null {
    return this.boundingVolume;
  }
  
  /**
   * Check if this object has a bounding volume for collision
   */
  public isCollidableObject(): boolean {
    return this.isCollidable && this.boundingVolume !== null;
  }
  
  /**
   * Check for collision with another game object
   * @param other The other game object to check collision with
   * @returns True if the objects are colliding
   */
  public collidesWith(other: GameObject): boolean {
    // If either object doesn't have a bounding volume, no collision
    if (!this.isCollidableObject() || !other.isCollidableObject()) {
      return false;
    }
    
    // Check collision between the bounding volumes
    return this.boundingVolume!.intersects(other.boundingVolume!);
  }
}
